import React, { FC, useEffect, useState } from 'react';
import { Button, Drawer, Menu } from 'antd';
import { BuyerCode, Divider, Image, InfiniteScroll, SearchInput, Spin } from 'common/components';
import { DEFAULT_PAGINATION_PAGE, LIST_LIMIT_20 } from 'common/config';
import { priceFormatter } from 'common/helpers';
import { Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ICreateGoodPayload, IGood, IGoodListPayload } from 'entities/Goods/Goods.models';
import {
  addGoodListItem,
  createGood,
  getGoodList,
  getGoodListChunk,
  getManuallyAddedGoodList,
  getManuallyAddedGoodListChunk,
  selectGoodHasMore,
  selectGoodList,
  selectIsGoodLoading,
} from 'entities/Goods/Good.slice';
import { CreateGoodsModal } from 'entities/Goods/components/CreateGoodsModal';

enum ETab {
  Catalog = 'catalog',
  ManuallyAdded = 'manually-added',
}

interface IComponentProps {
  open: boolean;
  loading?: boolean;
  allowManualAdd?: boolean;
  goodInBasket?: boolean;
  sellerId?: number;
  asUserId?: number;
  onCancel: () => void;
  onSelect: (good: IGood, onSuccess?: () => void) => void;
}

export const SelectGoodCard: FC<IComponentProps> = ({
  open,
  loading = false,
  allowManualAdd = false,
  goodInBasket,
  sellerId,
  asUserId,
  onCancel,
  onSelect,
}) => {
  const dispatch = useAppDispatch();
  // good
  const goodList = useAppSelector(selectGoodList);
  const goodHasMore = useAppSelector(selectGoodHasMore);
  const isGoodLoading = useAppSelector(selectIsGoodLoading);

  const [tab, setTab] = useState<ETab>(ETab.Catalog);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [openCreateGoodModal, setOpenCreateGoodModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [payload, setPayload] = useState<IGoodListPayload>({
    limit: LIST_LIMIT_20,
    offset: 0,
    goodInBasket,
    sellerId,
    asUserId,
  });

  useEffect(() => {
    if (!open) {
      setTab(ETab.Catalog);
      setPage(DEFAULT_PAGINATION_PAGE);
    }
  }, [open]);

  const isManuallyAddedTab = tab === ETab.ManuallyAdded;

  const handleCancel = (): void => {
    if (isGoodLoading) {
      return;
    }

    onCancel();
  };

  const onSearch = async (value?: string): Promise<void> => {
    const newGoodListPayload = {
      ...payload,
      search: value,
      offset: 0,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newGoodListPayload);

    if (isManuallyAddedTab) {
      await dispatch(getManuallyAddedGoodList(newGoodListPayload));
    } else {
      await dispatch(getGoodList(newGoodListPayload));
    }
  };

  const onTabChange = async (key: ETab): Promise<void> => {
    setTab(key);
    setFetchLoading(true);

    const newGoodListPayload = {
      ...payload,
      search: undefined,
      offset: 0,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newGoodListPayload);

    if (key === ETab.ManuallyAdded) {
      await dispatch(getManuallyAddedGoodList(newGoodListPayload));
    } else {
      await dispatch(getGoodList(newGoodListPayload));
    }

    setFetchLoading(false);
  };

  const selectGood = (good: IGood): void => {
    onSelect(good, handleCancel);
  };

  const handleOpenCreateGoodModal = (): void => {
    setOpenCreateGoodModal(true);
  };

  const closeCreateGoodModal = (): void => {
    setOpenCreateGoodModal(false);
  };

  const handleCreateGood = async (payload: ICreateGoodPayload): Promise<void> => {
    const good = await dispatch(createGood(payload));

    if (!good) {
      return;
    }

    if (isManuallyAddedTab) {
      dispatch(addGoodListItem(good));
    }

    onSelect(good);
  };

  return (
    <>
      <Drawer rootClassName="redesign drawer select-good-card" open={open} onClose={handleCancel} width={720} destroyOnClose>
        <div className="h-full flex direction-column">
          <div className="mb-14 ff-regular fs-xxl">Выберите товар</div>

          {allowManualAdd && (
            <div>
              <Menu
                mode="horizontal"
                items={[
                  { label: 'Товары из каталога', key: ETab.Catalog },
                  { label: 'Добавлены вручную', key: ETab.ManuallyAdded },
                ]}
                onClick={(e) => onTabChange(e.key as ETab)}
                selectedKeys={[tab]}
              />
            </div>
          )}

          <div className="mb-5 flex align-center">
            <SearchInput auto placeholder="Искать по названию или артикулу товара" clearDeps={[tab]} onSearch={onSearch} />

            {allowManualAdd && (
              <div className="ml-3">
                <Button className="button-sm primary" icon={<Plus className="icon-white" />} onClick={handleOpenCreateGoodModal}>
                  Новый товар
                </Button>
              </div>
            )}
          </div>

          <InfiniteScroll
            loading={scrollLoading}
            hasMore={!isGoodLoading && goodHasMore}
            loader={<Spin />}
            scrollToTopTrigger={[open, payload]}
            loadMore={async () => {
              setScrollLoading(true);
              setPage(page + 1);
              if (isManuallyAddedTab) {
                await dispatch(getManuallyAddedGoodListChunk({ ...payload, offset: LIST_LIMIT_20 * page }));
              } else {
                await dispatch(getGoodListChunk({ ...payload, offset: LIST_LIMIT_20 * page }));
              }
              setScrollLoading(false);
            }}
          >
            {goodList.length ? (
              <Spin spinning={fetchLoading || loading}>
                {goodList.map((good, index) => {
                  return (
                    <div key={good.id}>
                      <Button className="select-good-card__goods" onClick={() => selectGood(good)}>
                        <Image size={80} src={good.images[0]} />

                        <div className="select-good-card__goods-container">
                          <div className="mb-5 ff-medium fs-s color-black">{good.name}</div>

                          <div className="select-good-card__goods-footer">
                            <div>
                              {good.sellerCode && (
                                <span className="mr-6 ff-regular fs-xs color-dark-grey">{good.sellerCode}</span>
                              )}

                              <BuyerCode code={good.buyerCode} />
                            </div>

                            <span className="ff-semibold fs-m color-black">{priceFormatter(good.price)}</span>
                          </div>
                        </div>
                      </Button>

                      <Divider marginY={8} show={index !== goodList.length - 1} />
                    </div>
                  );
                })}
              </Spin>
            ) : (
              <div className="flex align-center justify-center flex-1 ff-regular fs-xs color-dark-grey">Список пуст</div>
            )}
          </InfiniteScroll>
        </div>
      </Drawer>

      <CreateGoodsModal
        open={openCreateGoodModal}
        loading={isGoodLoading}
        onCancel={closeCreateGoodModal}
        onSubmit={handleCreateGood}
      />
    </>
  );
};
